import React, { useEffect, useState } from 'react';

import Flag from 'react-flags';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IoArrowBackOutline } from "react-icons/io5";

import Yup from '../../../../utils/yupExtensions';
import styles from './EmployerPortalApplications.module.css';
import BubbleButton from '../../Buttons/BubbleButton/BubbleButton';
import GlobalModal from '../../Popups/GlobalModal';
import FormSelectField from '../../UpdatedFormUI/FormSelectField/FormSelectField';
import userAvatarPlaceholder from '../../../../assets/images/other/user_avatar_placeholder.png';
import { getTimeAgo } from '../../../../utils/formatHelpers';
import { Typography } from '../../../Typography/typography';
import { createChatroom } from '../../../../services/chatService';
import { useGetSkillsHook } from '../../../../utils/utilityHooks';
import { updateApplicationField } from '../../../../services/jobService';
import { checkProfileViewPermission, recordProfileView } from '../../../../services/profileViewService';
import {
    BriefcaseWhite,
    ClockBlue,
    GenderIcon,
    LanguageIcon,
    Message,
    PasportIcon,
    Pending,
    RejectFilled,
    RejectIcon,
    ShortListFilled,
    ShortListIcon,
    Skills,
    UpArrow,
    VerifyGreenBadge,
    WorkHistory,
} from '../../../../assets/images/icons';

function ApplicationsDetail({ applicationDetail, setApplicationDetail, isMobile }) {
    const [selectedTab, setSelectedTab] = useState('Resume');
    const [isArrowUp, setIsArrowUp] = useState(true); // State to manage arrow rotation
    const [value, setUpdateStatus] = useState(''); // State to manage arrow rotation
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeMessage, setUpgradeMessage] = useState('');
    const [viewLoading, setViewLoading] = useState(false);
    const userToken = useSelector(state => state.user.token);
    const navigate = useNavigate();
    const features = useSelector(state => state.user.features);
    const canViewDownloadResume = features && features.viewDownloadResume > 0;

    const jobseeker = applicationDetail?.jobseeker || {};
    const application = applicationDetail?.application || {};
    const skills = useGetSkillsHook();
    const [isPdfVisible, setIsPdfVisible] = useState(true); // State to manage PDF visibility
    const [userRoleSkills, setUserRoleSkills] = useState([]);
     let last_seen = '';
    let is_online = false;
    const user = useSelector(state => state.user.user);
    const date_info = getTimeAgo(new Date(jobseeker.last_visit));
    if (
        date_info.unit_plural !== 'month' &&
        date_info.unit_plural !== 'months' &&
        date_info.unit_plural !== 'year' &&
        date_info.unit_plural !== 'years'
    ) {
        if (
            date_info.unit_plural === 'second' ||
            date_info.unit_plural === 'seconds' ||
            date_info.unit_plural === 'minute' ||
            (date_info.unit_plural === 'minutes' && date_info.count < 5)
        ) {
            last_seen = t('general.UI.online');
            is_online = true;
        } else {
            last_seen =
                t('employer.subscription_form.active') +
                ' ' +
                date_info.count +
                ' ' +
                t('general.UI.' + date_info.unit_plural) +
                ' ' +
                t('general.UI.ago');
        }
    }

    useEffect(() => {
         // Format role-based skills for display
        const roleSkillsData = jobseeker?.roleBasedSkills || [];
        setUserRoleSkills(roleSkillsData);
    }, [jobseeker]);

    const profileDataUI = (icon, title, desc) => {
        return (
            <div className={styles.profileDataContainer}>
                <div>
                    <div className={styles.dataIconContainer}>
                        <img src={icon} alt={`profile data icon`}
                            className={styles.profileDataIcon}
                        />
                    </div>
                </div>
                <div className={styles.col}>
                    <p className={styles.profileDataValue}>{title}</p>
                    <p className={styles.profileDataTitle}>{desc}</p>
                </div>
            </div>
        );
    };

    const handleArrowClick = () => {
        if (!canViewDownloadResume) {
            setShowUpgradeModal(true);
            return;
        }
        setIsArrowUp((prev) => !prev);
        setIsPdfVisible((prev) => !prev);
    };

    const handleDownloadPdf = async () => {
        // Check if user has the feature to view/download resume
        if (!canViewDownloadResume) {
            setShowUpgradeModal(true);
            return;
        }

        const pdfUrl =
            selectedTab === 'Resume' ?
                process.env.REACT_APP_UPLOADS_PATH + application.proposal_cv :
                process.env.REACT_APP_UPLOADS_PATH + application.proposal_cover_letter;
        try {
            const response = await fetch(pdfUrl);
            if (!response.ok) {
                throw new Error('Failed to download PDF');
            }
            const blob = await response.blob();
            
            // Create a download link
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            
            // Set the filename based on the selected tab and jobseeker name
            const filename = `${jobseeker.first_name}_${jobseeker.last_name}_${selectedTab.replace(' ', '_')}.pdf`;
            downloadLink.download = filename;
            
            // Trigger the download
            document.body.appendChild(downloadLink);
            downloadLink.click();
            
            // Clean up
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(downloadLink.href);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        }
    };

    let education;
    if (jobseeker?.education) education = jobseeker?.education[0]?.education_title
    if (jobseeker?.education[0]?.institution) {
        education = education + ", " + jobseeker?.education[0]?.institution
    }
    const statusOptions = [
        { value: 'New', label: t('applicationStatus.New') },
        { value: 'Shortlisted', label: t('applicationStatus.Shortlisted') },
        { value: 'Reviewing', label: t('applicationStatus.Reviewing') },
        { value: 'Interviewing', label: t('applicationStatus.Interviewing') },
        { value: 'Hired', label: t('applicationStatus.Hired') },
        { value: 'Rejected', label: t('applicationStatus.Rejected') },
        { value: 'Undecided', label: t('applicationStatus.Undecided') },
    ];
    const formik = useFormik({
        initialValues: {
            status: applicationDetail?.application?.status || '',
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => { },
    });
    const handleStatusChange = (status) => {
        updateApplicationField({
            application_id: application.id,
            token: userToken,
            field: 'status',
            value: status
        })
            .then((response) => {
                formik.setFieldValue('status', status);
                application.status = status
            })
            .catch((error) => {
                console.log(error);
            });
    }
    useEffect(() => {
        if (formik?.values?.status?.length > 0) handleStatusChange(formik.values.status)
    }, [formik.values.status])
    useEffect(() => {
        formik.setFieldValue('status', applicationDetail?.application?.status);
    }, [applicationDetail?.application?.status])

    const handleViewProfile = async () => {
        // If no access (value = 0), immediately show upgrade modal
        if (features.viewApplicantProfile === 0) {
            setUpgradeMessage(t('general.UI.upgrade_plan_to_view_profile', { defaultValue: 'Upgrade your plan to view applicant profiles' }));
            setShowUpgradeModal(true);
            return;
        }

        setViewLoading(true);

        try {
            // If unlimited access (value 1), navigate directly
            if (features.viewApplicantProfile === 1) {
                navigate(`/jobseekers/${jobseeker.slug}`);
                return;
            }

            // For limited access (value > 1), check with backend API first
            if (features.viewApplicantProfile > 1 && user?.id && jobseeker?.id) {
                try {
                    // First check if the user is allowed to view more profiles
                    const permissionResult = await checkProfileViewPermission({
                        employerId: user.id,
                        jobseekerId: jobseeker.id,
                        features,
                        token: userToken
                    });

                    // If already viewed or can view more profiles, allow access
                    if (permissionResult.canView) {
                        // If this is a new view (not already viewed), record it
                        if (!permissionResult.alreadyViewed) {
                            await recordProfileView({
                                employerId: user.id,
                                jobseekerId: jobseeker.id,
                                token: userToken
                            });
                        }

                        // Navigate to profile
                        navigate(`/jobseekers/${jobseeker.slug}`);
                    } else {
                        // Cannot view more profiles - show upgrade modal
                        const limitMessage = t(
                            'general.UI.profile_view_limit_reached',
                            {
                                limit: features.viewApplicantProfile,
                                defaultValue: `You've reached your limit of ${features.viewApplicantProfile} profile views per month. Upgrade your plan for unlimited access.`
                            }
                        );
                        setUpgradeMessage(limitMessage);
                        setShowUpgradeModal(true);
                    }
                } catch (viewError) {
                    console.error("Error checking profile view permission:", viewError);
                    // On error, just navigate to the profile (fail open approach)
                    navigate(`/jobseekers/${jobseeker.slug}`);
                }
            } else {
                // If missing data, just navigate to profile
                navigate(`/jobseekers/${jobseeker.slug}`);
            }
        } catch (error) {
            console.error('Error in profile view flow:', error);
            // Fallback to allowing access in case of errors
            navigate(`/jobseekers/${jobseeker.slug}`);
        } finally {
            setViewLoading(false);
        }
    };

    return (
        <div className={styles.detailContainer}>
            <div className={styles.detailHeader}>
                {isMobile ?
                    <div className={styles.row} style={{ paddingLeft: `0px` }}>
                        <IoArrowBackOutline style={{ cursor: "pointer" }}
                            onClick={() => { setApplicationDetail(null) }}
                            size={20} />
                        <Typography title={t(`general.UI.Application_Details`)} fontSize={16} weight={600} style={{ textWrap: "noWrap" }} />
                    </div> :

                    <Typography title={t(`general.UI.Application_Details`)} fontSize={16} weight={600} style={{ textWrap: "noWrap" }} />
                }
                <div className={`${styles.row} ${styles.statusWrapper}`} >
                    <div className={styles.statusShortcutContainer}>
                        <img
                            src={formik.values.status === 'Shortlisted' ? ShortListFilled : ShortListIcon}
                            onClick={() => {
                                handleStatusChange(`Shortlisted`)
                                setUpdateStatus(`Shortlisted`)
                            }}
                            className={
                                formik.values.status === 'Shortlisted' ? styles.iconContainerSelected : styles.iconContainer
                            }
                        />
                        <img
                            src={Pending}
                            onClick={() => {
                                handleStatusChange(`Undecided`)
                                setUpdateStatus(`Undecided`)
                            }}
                            className={
                                formik.values.status === 'Undecided'
                                    ? `${styles.iconContainer} ${styles.iconPending} ${styles.iconPendingSelected}`
                                    : `${styles.iconContainer} ${styles.iconPending}`
                            }
                        />
                        <img
                            src={formik.values.status === 'Rejected' ? RejectFilled : RejectIcon}
                            onClick={() => {
                                handleStatusChange(`Rejected`)
                                setUpdateStatus(`Rejected`)
                            }}
                            className={
                                formik.values.status === 'Rejected'
                                    ? styles.iconContainerSelectedForRejection
                                    : styles.iconContainer
                            }
                        />
                    </div>
                    <div className={styles.statusDropdownContainer}>
                        <FormSelectField
                            name="status"
                            placeholder={t('general.UI.select')}
                            type="default"
                            hasBorder
                            options={statusOptions}
                            onFormikChange={formik.handleChange}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: '#F2FBFE',
                                }),
                            }}
                            value={formik.values.status}
                            themeLight
                            error={formik.touched.status && formik.errors.status}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.userInfo}>
                <div className={`${styles.applicationItemForDetail} ${styles.userInfoWrapper}`}>
                    {jobseeker?.profile_image ? (
                        <img
                            src={process.env.REACT_APP_UPLOADS_PATH + jobseeker?.profile_image}
                            alt={jobseeker?.profile_image}
                            className={styles.userPhoto}
                            onError={(e) => {
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = userAvatarPlaceholder; // Set fallback image
                            }}
                        />
                    ) : (
                        <img src={userAvatarPlaceholder} className={styles.userPhoto} onError={(e) => {
                            e.target.onerror = null; // Prevent infinite loop
                            e.target.src = userAvatarPlaceholder; // Set fallback image
                        }} />
                    )}
                    <div>
                        {jobseeker && (
                            <div className={`${styles.namedWrapper}`}>
                                <Typography
                                    fontSize={15}
                                    weight={600}
                                    color={'#093559'}
                                    title={jobseeker?.first_name + jobseeker?.last_name}
                                    style={{ cursor: viewLoading ? 'wait' : 'pointer' }}
                                    onClick={handleViewProfile}
                                />
                                {jobseeker.verification_status && (
                                    <img src={VerifyGreenBadge} className={styles.verificationBadge} />
                                )}
                            </div>
                        )}
                        <div className={styles.flagContainer} style={{ gap: '10px', display: 'flex', alignItems: 'center' }}>
                            {/* Country and City section */}
                            {(jobseeker?.country || jobseeker?.city) && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {jobseeker?.country && (
                                        <Flag
                                            name={jobseeker?.country}
                                            format="svg"
                                            shiny={false}
                                            basePath="/vendor/flags"
                                            style={{ marginRight: '5px' }}
                                        />
                                    )}
                                    <span>
                                        {jobseeker?.country && t('general.country.' + jobseeker?.country)}
                                        {jobseeker?.country && jobseeker?.city && ', '}
                                        {jobseeker?.city && t('general.city.' + jobseeker?.city)}
                                    </span>
                                </div>
                            )}
                            
                            {/* Separator */}
                            {((jobseeker?.country || jobseeker?.city) && jobseeker?.nationality) && 
                                <div className={styles.saperatorGender}></div>
                            }
                            
                            {/* Nationality section */}
                            {jobseeker?.nationality && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <img src={PasportIcon} className={styles.icon} />
                                    <span>
                                        <Typography
                                            title={t('general.nationality.' + jobseeker?.nationality)}
                                            color={'#6D6D6D'}
                                            fontSize={13}
                                            weight={400}
                                        />
                                    </span>
                                </div>
                            )}
                            
                            {/* Separator */}
                            {jobseeker?.nationality && jobseeker?.gender && 
                                <div className={styles.saperatorGender}></div>
                            }
                            
                            {/* Gender section */}
                            {jobseeker?.gender && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <img src={GenderIcon} className={styles.icon} />
                                    <span>
                                        <Typography
                                            title={t('general.UI.' + jobseeker?.gender.toLowerCase())}
                                            color={'#6D6D6D'}
                                            fontSize={13}
                                            weight={400}
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.msgAndStatusWrapper}>
                    <div className={`${styles.row} ${styles.msgIconWrapper}`}>
                        {is_online ? (
                            <>
                                <div className={styles.greenCircle}></div>
                                <p className={styles.online}>{t('TalentCard.online')}</p>
                            </>
                        ) : (
                            last_seen && (
                                <>
                                    <img src={ClockBlue} />
                                    <Typography title={last_seen} color={'#6D6D6D'} fontSize={14} weight={400} />
                                </>
                            )
                        )}
                    </div>
                    <div className={styles.msgIconContainer}>
                        {features && features.contactApplicant > 0 ? (
                            <img
                                onClick={async () => {
                                    await createChatroom(user.id, jobseeker.id);
                                    navigate('/chat', {
                                        state: {
                                            employerId: user.id,
                                            jobSeekerId: jobseeker.id,
                                        }
                                    })
                                }}
                                src={Message} className={styles.messageIcon}
                            />
                        ) : (
                            <div className={styles.disabledMessageIcon} title={t('general.UI.upgrade_plan_to_contact')}>
                                <img src={Message} className={`${styles.messageIcon} ${styles.disabled}`} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.featureWrapper}>
                <div className={`${styles.cardcol} ${styles.cardcolWithoutPadding}`} >
                    {profileDataUI(BriefcaseWhite, t('jobRequirements.education'), education)}
                    {jobseeker?.languages?.length > 0 && profileDataUI(LanguageIcon, t('TalentCard.languages'),
                        jobseeker?.languages
                            ?.map((language) => t('general.language.' + language.languageCode))
                            .join(", ")
                    )}
                </div>
                <div className={`${styles.cardcol} ${styles.cardcolWithoutPadding}`} >
                    {profileDataUI(WorkHistory, t('jobRequirements.workExperience'), jobseeker.years_of_experience + " " + t("general.UI.years"))}
                    {userRoleSkills?.length > 0 && profileDataUI(Skills, t('job_seeker.role_based_skills'),
                        userRoleSkills
                            ?.flatMap(roleSkill => roleSkill.skills || [])
                            .filter(Boolean)
                            .join(", ")
                    )}
                </div>
            </div>

            <div className={styles.pdfMainContainer}>
                <div className={styles.pdfHeader}>
                    <div className={styles.resumeButtonWrapper}>
                        <div
                            className={`${styles.resumeButton} ${selectedTab == 'Resume' ? styles.activeButtonStyle : ''}`}
                            onClick={() => { setSelectedTab('Resume') }}
                        >
                            <Typography title={t('general.UI.Resume')} fontSize={16} weight={selectedTab == 'Resume' ? 600 : 400} color={selectedTab == 'Resume' ? "#0464B2" : '#6D6D6D'} />
                        </div>
                        <div className={styles.saperator} />
                        <div
                            className={`${styles.resumeButton} ${selectedTab == 'Cover Letter' ? styles.activeButtonStyle : ''}`}
                            onClick={() => { setSelectedTab('Cover Letter') }}
                        >
                            <Typography title={t('general.UI.Cover_Letter')} fontSize={16} weight={selectedTab == 'Cover Letter' ? 600 : 400} color={selectedTab == 'Cover Letter' ? "#0464B2" : "#6D6D6D"} />
                        </div>
                    </div>
                    <div className={styles.pdfButtonWrapper}>
                        <BubbleButton
                            dark
                            headerDarkButton
                            onClick={handleDownloadPdf} // Call the download function
                            style={{ height: "100%" }}
                        >
                            {t('general.UI.Download_PDF')}
                        </BubbleButton>
                        <img
                            src={UpArrow}
                            onClick={handleArrowClick} // Add click handler
                            style={{
                                cursor: 'pointer',
                                transform: isArrowUp ? 'rotate(0deg)' : 'rotate(180deg)', // Rotate the arrow
                                transition: 'transform 0.3s ease', // Smooth transition
                            }}
                        />
                    </div>
                </div>

                {canViewDownloadResume ? (
                    <div
                        className={styles.cvWrapper}
                        style={{
                            height: isPdfVisible ? '460px' : '0px',
                            overflow: 'hidden',
                            transition: 'height 0.3s ease', // Smooth transition for height
                        }}
                    >
                        {selectedTab === 'Resume' ? (
                            isPdfVisible && <iframe
                                className={styles.pdf}
                                src={process.env.REACT_APP_UPLOADS_PATH + application.proposal_cv + "#toolbar=0"}
                            ></iframe>
                        ) : (
                            isPdfVisible && <iframe
                                className={styles.pdf}
                                src={process.env.REACT_APP_UPLOADS_PATH + application.proposal_cover_letter + "#toolbar=0"}
                            ></iframe>
                        )}
                    </div>
                ) : (
                    <div className={styles.upgradePromptContainer}>
                        <BubbleButton
                            dark
                            headerDarkButton
                            onClick={() => setShowUpgradeModal(true)}
                            style={{ padding: '15px 30px' }}
                        >
                            {selectedTab === 'Resume'
                                ? t('general.UI.view_resume')
                                : t('general.UI.view_cover_letter')}
                        </BubbleButton>
                    </div>
                )}
            </div>

            <GlobalModal
                show={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title={t('general.subscription.upgrade')}
                message={upgradeMessage || t('general.UI.upgrade_plan_to_view_download_resume')}
                primaryButtonText={t('general.subscription.upgradeNow')}
                secondaryButtonText={t('general.UI.cancel', { defaultValue: 'Cancel' })}
                navigateTo="/subscriptions"
                secondaryButtonAction={() => setShowUpgradeModal(false)}
            />
        </div>
    );
}

export default ApplicationsDetail;